import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { format, parseISO } from "date-fns";
import { Search, ExternalLink } from "lucide-react";
import { AppHeader } from "@/components/app/AppHeader";
import { KpiRow } from "@/components/app/KpiRow";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAllProfiles, useAllBrands } from "@/hooks/use-admin-data";
import { useTeamEntries } from "@/hooks/use-team-entries";
import { useAuth, DEPARTMENT_LABELS, type Department } from "@/lib/auth-context";
import { DEPARTMENT_CONFIGS } from "@/lib/department-configs";
import { modCanDept, type ModeratorPermissions } from "@/lib/moderator-permissions";
import { TaskReviewModal, type TeamTaskMode } from "@/components/team/TaskReviewModal";
import {
  filterByDate, filterByStatus, searchRows, statusUiLabel, statusPillStyle, exportCsv,
  type DateFilter, type StatusFilter,
} from "@/components/team/team-entries";
import type { Submission } from "@/lib/submissions";
import { isCreativeReviewDept } from "@/lib/dept-helpers";
import { formatIst } from "@/lib/tz";

const ALL_DEPTS = Object.keys(DEPARTMENT_CONFIGS) as Department[];

const searchSchema = z.object({
  dept: z.enum(ALL_DEPTS as [Department, ...Department[]]).optional(),
});

export const Route = createFileRoute("/_app/_team/team/departments")({
  validateSearch: (s) => searchSchema.parse(s),
  component: TeamDepartmentsPage,
});

function TeamDepartmentsPage() {
  const { dept: deptParam } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { profile, role } = useAuth();
  const perms = (profile?.moderator_permissions ?? {}) as ModeratorPermissions;
  const isAdmin = role === "admin";
  const ownDeptOnly =
    role === "moderator" && Boolean(perms.scope_own_department) && profile?.department;

  const visibleDepts = isAdmin
    ? ALL_DEPTS
    : ownDeptOnly
      ? [profile!.department as Department]
      : ALL_DEPTS.filter((d) => modCanDept(perms, d, "view"));

  const defaultDept: Department | undefined =
    (deptParam && visibleDepts.includes(deptParam) ? deptParam : visibleDepts.includes("scriptwriter") ? "scriptwriter" : visibleDepts[0]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [brandFilter, setBrandFilter] = useState<string>("all");
  const [dateFilter, setDateFilter] = useState<DateFilter>("all");
  const [pendingOnly, setPendingOnly] = useState(false);
  const [modalEntry, setModalEntry] = useState<(Submission & { employee_name?: string }) | null>(null);
  const [modalMode, setModalMode] = useState<TeamTaskMode>("view");

  const { data: profiles = [] } = useAllProfiles();
  const { data: submissions = [] } = useTeamEntries();
  const { data: brands = [] } = useAllBrands();

  const profilesById = useMemo(() => new Map(profiles.map((p) => [p.id, p])), [profiles]);
  const dept = defaultDept;

  const deptRows = useMemo(() => {
    if (!dept) return [];
    return submissions.filter((s) => s.department === dept);
  }, [submissions, dept]);

  const filtered = useMemo(() => {
    let r = filterByDate(deptRows, dateFilter);
    r = filterByStatus(r, statusFilter);
    if (brandFilter !== "all") r = r.filter((x) => (x.brand ?? "") === brandFilter);
    r = searchRows(r, profilesById, search);
    if (pendingOnly) r = r.filter((x) => x.status === "pending" || x.status === "correction_needed");
    return r;
  }, [deptRows, dateFilter, statusFilter, brandFilter, search, pendingOnly, profilesById]);

  const kpi = {
    total: filtered.length,
    completed: filtered.filter((x) => x.status === "approved").length,
    pending: filtered.filter((x) => x.status === "pending").length,
    corrections: filtered.filter((x) => x.status === "correction_needed").length,
    overdue: filtered.filter((x) => statusUiLabel(x.status, x.submission_date).key === "overdue").length,
  };

  const deptPeople = dept ? profiles.filter((p) => p.department === dept) : [];

  if (visibleDepts.length === 0 || !dept) {
    return (
      <>
        <AppHeader subtitle="By Department" />
        <div className="px-[22px] pb-6 text-[12px] text-muted-foreground">
          You don't have view permission for any department.
        </div>
      </>
    );
  }

  const canReview = isAdmin || role === "moderator";
  const deptIsCreative = isCreativeReviewDept(dept);

  return (
    <>
      <AppHeader subtitle="Department-wise task updates by employee" />
      <div className="px-[22px] pb-6">
        {/* Top controls */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative min-w-[280px] flex-1">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search task title, employee, product..." className="h-9 pl-7 text-[13px]" />
          </div>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
            <SelectTrigger className="h-9 w-[120px] text-[12px]"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="done">Done</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="review">Review</SelectItem>
              <SelectItem value="correction">Correction</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
          <Select value={brandFilter} onValueChange={setBrandFilter}>
            <SelectTrigger className="h-9 w-[140px] text-[12px]"><SelectValue placeholder="Brand" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Brands</SelectItem>
              {brands.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={dateFilter} onValueChange={(v) => setDateFilter(v as DateFilter)}>
            <SelectTrigger className="h-9 w-[130px] text-[12px]"><SelectValue placeholder="Date" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
          <Button
            className="h-9 bg-[#7C3AED] text-white hover:bg-[#6D28D9]"
            onClick={() => exportCsv(`department-${dept}-${formatIst(new Date(), "yyyyMMdd")}.csv`, filtered, profilesById)}
          >
            Export
          </Button>
          <Button
            className="h-9 bg-[#18181B] text-white hover:bg-black"
            onClick={() => setPendingOnly((p) => !p)}
          >
            {pendingOnly ? "Show All" : "View Pending Only"}
          </Button>
        </div>

        {/* Department pills */}
        <div className="mb-4 flex flex-wrap gap-2">
          {visibleDepts.map((d) => {
            const active = d === dept;
            return (
              <button
                key={d}
                type="button"
                onClick={() => navigate({ search: { dept: d } })}
                className={`h-8 rounded-md border px-3 text-[12px] font-medium transition-colors ${
                  active
                    ? "border-[#18181B] bg-[#18181B] text-white"
                    : "border-border bg-card text-foreground hover:bg-muted/40"
                }`}
              >
                {DEPARTMENT_LABELS[d]}
              </button>
            );
          })}
        </div>

        {/* KPI row */}
        <KpiRow
          items={
            deptIsCreative
              ? [
                  { label: "Total Updates", value: String(kpi.total), trend: `${DEPARTMENT_LABELS[dept]} entries`, variant: "primary" },
                  { label: "Completed", value: String(kpi.completed), trend: "approved work" },
                  { label: "Pending", value: String(kpi.pending), trend: "awaiting review" },
                  { label: "Corrections", value: String(kpi.corrections), variant: "danger", trend: "needs rework" },
                  { label: "Overdue", value: String(kpi.overdue), variant: "danger", trend: "past deadline" },
                ]
              : [
                  { label: "Total Updates", value: String(kpi.total), trend: `${DEPARTMENT_LABELS[dept]} entries`, variant: "primary" },
                  { label: "Completed", value: String(kpi.completed), trend: "logged work" },
                  { label: "Pending", value: String(kpi.pending), trend: "in progress" },
                  { label: "Output Count", value: String(kpi.total - kpi.overdue), trend: "tracked entries" },
                  { label: "Missed Updates", value: String(kpi.overdue), variant: "danger", trend: "no log on date" },
                ]
          }
        />

        {/* Main grid */}
        <div className="mt-2 grid gap-4 lg:grid-cols-[1fr_280px]">
          <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
            <div className="text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
              {DEPARTMENT_LABELS[dept].toUpperCase()}
            </div>
            <h2 className="mb-3 text-[18px] font-bold">Task Update Entries</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="border-b border-border bg-muted/40 text-[10px] uppercase tracking-[0.4px] text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Date</th>
                    <th className="px-3 py-2 text-left font-semibold">Employee</th>
                    <th className="px-3 py-2 text-left font-semibold">Task Title</th>
                    <th className="px-3 py-2 text-left font-semibold">Type</th>
                    <th className="px-3 py-2 text-left font-semibold">Product</th>
                    <th className="px-3 py-2 text-left font-semibold">Brand</th>
                    <th className="px-3 py-2 text-left font-semibold">Status</th>
                    <th className="px-3 py-2 text-left font-semibold">Link</th>
                    <th className="px-3 py-2 text-left font-semibold">Correction</th>
                    <th className="px-3 py-2 text-left font-semibold">Reviewer</th>
                    <th className="px-3 py-2 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={11} className="px-3 py-8 text-center text-muted-foreground">No task entries found for this filter.</td></tr>
                  )}
                  {filtered.map((r) => {
                    const p = profilesById.get(r.user_id);
                    const reviewer = r.reviewed_by ? profilesById.get(r.reviewed_by) : null;
                    const det = (r.details ?? {}) as Record<string, unknown>;
                    const link = (det.link as string) ?? (det.drive_link as string) ?? (det.creative_drive_link as string);
                    const taskType = (det.type as string) ?? (det.task_type as string) ?? "—";
                    const ui = statusUiLabel(r.status, r.submission_date);
                    const pill = statusPillStyle(ui.key);
                    return (
                      <tr key={r.id} className="border-b border-border last:border-0 hover:bg-muted/30" style={{ height: 48 }}>
                        <td className="px-3 py-2">{format(parseISO(r.submission_date), "dd MMM yyyy")}</td>
                        <td className="px-3 py-2">{p?.full_name ?? "—"}</td>
                        <td className="px-3 py-2 font-medium">{r.title}</td>
                        <td className="px-3 py-2">{taskType}</td>
                        <td className="px-3 py-2">{r.product ?? "—"}</td>
                        <td className="px-3 py-2">{r.brand ?? "—"}</td>
                        <td className="px-3 py-2">
                          <span style={{ background: pill.bg, color: pill.fg, padding: "2px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 600 }}>
                            {ui.label}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {link ? (
                            <a href={link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary underline">
                              {link.length > 18 ? link.slice(0, 18) + "…" : link}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : "—"}
                        </td>
                        <td className="px-3 py-2">{deptIsCreative ? (r.correction_notes ? "Note" : "None") : "—"}</td>
                        <td className="px-3 py-2">{deptIsCreative ? (reviewer?.full_name ?? "—") : "—"}</td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex gap-1">
                            <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => { setModalEntry({ ...r, employee_name: p?.full_name }); setModalMode("view"); }}>View</Button>
                            {deptIsCreative && canReview && (
                              <Button size="sm" className="h-7 bg-[#18181B] px-2 text-[11px] text-white hover:bg-black" onClick={() => { setModalEntry({ ...r, employee_name: p?.full_name }); setModalMode("review"); }}>Review</Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
            <div className="text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">Roster</div>
            <h2 className="text-[18px] font-bold">Department Roster</h2>
            <div className="mt-3 flex flex-col gap-2">
              {deptPeople.length === 0 && <p className="text-[12px] text-muted-foreground">No people.</p>}
              {deptPeople.map((p) => (
                <div key={p.id} className="rounded-md border border-border px-3 py-2 text-[12px] font-medium">{p.full_name}</div>
              ))}
            </div>
            <div className="mt-3 text-[11px] text-muted-foreground">{deptPeople.length} {deptPeople.length === 1 ? "employee" : "employees"}</div>
          </div>
        </div>
      </div>

      <TaskReviewModal
        open={!!modalEntry}
        onClose={() => setModalEntry(null)}
        mode={modalMode}
        entry={modalEntry}
      />
    </>
  );
}
